import {
  Checkbox,
  FormControlLabel,
  createStyles,
  Divider,
  WithStyles,
  withStyles,
} from "@material-ui/core";
import { CircularProgress } from "@mui/material";
import { useTranslations } from "adapters/i18n";
import { PortalRoutes } from "constants/routes";
import history from "features/app/History";
import BannerAlert from "features/shared/components/BannerAlert/bannerAlert";
import SkeletonLoading from "features/shared/components/SkeletonLoading";
import React, { type ComponentType } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import * as actions from "redux/actions";
import { resetBenchmarkEditUserAction } from "redux/actions/benchmarkActions";
import {
  fetchBenchmarkAssociacoes,
  fetchBenchmarkStatusForEditUser,
  grantRevokeBenchmarkAccess,
} from "redux/operations/benchmarkOperations";
import type {
  BenchmarkClienteFazendas,
  BenchmarkGrantBody,
  BenchmarkLocalUserStatus,
  BenchmarkRevokeBody,
  BenchmarkUserStatusResponse,
} from "services/BenchmarkService";
import type { NotifyFn } from "../../../userManagement.types";
import {
  FarmAndRetreatLinkForm,
  FormActions,
  UserDataForm,
  PermissionInfoCard,
  Title,
} from "./components";
import { useUserForm } from "./hooks/useUserForm";
import { RootState } from "redux/reducers";

type StateProps = ReturnType<typeof mapStateToProps>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type OwnProps = { userId?: number | null };
type UserFormProps = OwnProps &
  StateProps &
  DispatchProps &
  WithStyles<typeof styles>;

function UserForm({
  classes,
  benchmarkLocalUserStatus,
  benchmarkAssociacoes,
  benchmarkCarregando,
  notifyError,
  notifySuccess,
  setLoading: setPageLoading,
  resetBenchmarkEditUser,
  fetchBenchmarkAssociacoes: fetchBenchmarkAssociacoesForUser,
  fetchBenchmarkStatus,
  grantRevokeBenchmarkAccess: grantRevokeBenchmark,
  userId,
}: UserFormProps) {
  const { t } = useTranslations();

  const handleNotify: NotifyFn = (message, variant) => {
    if (variant === "success") {
      notifySuccess({ mensagem: message });
      return;
    }

    notifyError({ mensagem: message });
  };

  const toManagementPage = () => {
    history.push(PortalRoutes.USER_MANAGEMENT);
  };

  const {
    formState,
    handleAddFarmAndRetreatLink,
    handleBlur,
    handleCancel,
    handleChangeForm,
    handleRemoveFarmAndRetreatLink,
    handleSubmit,
    isEditing,
    isLoading,
  } = useUserForm({
    onNotify: handleNotify,
    setPageLoading,
    userId,
    toManagementPage,
    onResetBenchmarkEditUser: resetBenchmarkEditUser,
    onFetchBenchmarkAssociacoes: fetchBenchmarkAssociacoesForUser,
    onFetchBenchmarkStatus: fetchBenchmarkStatus,
    onGrantRevokeBenchmarkAccess: grantRevokeBenchmark,
    benchmarkAssociacoes,
  });

  if (isLoading) {
    return <UserFormLoading />;
  }

  const benchmarkChecked = formState.canAccessBenchmark.value;

  const showBenchmarkAlert =
    isEditing &&
    benchmarkChecked &&
    (benchmarkLocalUserStatus === "inactive" ||
      benchmarkLocalUserStatus === "not_found");

  return (
    <div>
      <Title isEditing={isEditing} />
      <UserDataForm
        isEditing={isEditing}
        onFieldBlur={handleBlur}
        onChangeField={handleChangeForm}
        formState={formState}
      />
      <Divider className={classes.divider} />
      <FarmAndRetreatLinkForm
        formData={formState.farmAndRetreatLinks}
        onAddLink={handleAddFarmAndRetreatLink}
        onRemoveLink={handleRemoveFarmAndRetreatLink}
      />
      {showBenchmarkAlert && (
        <>
          <Divider className={classes.divider} />
          <div className={classes.benchmarkSection}>
            <BenchmarkAccessAlert
              localUserStatus={benchmarkLocalUserStatus}
              associacoes={benchmarkAssociacoes}
              carregando={benchmarkCarregando}
              classes={classes}
            />
          </div>
        </>
      )}
      <Divider className={classes.divider} />
      <PermissionInfoCard />
      <FormActions
        isEditing={isEditing}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

type BenchmarkAccessAlertProps = {
  localUserStatus: BenchmarkLocalUserStatus;
  associacoes: BenchmarkClienteFazendas[];
  carregando: boolean;
  classes: WithStyles<typeof styles>["classes"];
};

function BenchmarkAccessAlert({
  localUserStatus,
  associacoes,
  carregando,
  classes,
}: BenchmarkAccessAlertProps) {
  const { t } = useTranslations();

  if (localUserStatus === "inactive") {
    return (
      <BannerAlert type="warning" className={classes.benchmarkAlert}>
        <span>{t("benchmark:grant-benchmark-access-alert-inactive")}</span>
      </BannerAlert>
    );
  }

  return (
    <BannerAlert type="info" className={classes.benchmarkAlert}>
      {carregando ? (
        <CircularProgress size={20} />
      ) : (
        <div>
          <span>{t("benchmark:grant-benchmark-access-alert-not-found")}</span>
          {associacoes.length === 0 ||
          associacoes.every((a) => a.fazendas.every((f) => !f.has_access)) ? (
            <p className={classes.benchmarkNoAssociations}>
              {t("benchmark:grant-benchmark-access-no-associations")}
            </p>
          ) : (
            <ul className={classes.benchmarkFarmList}>
              {associacoes.map((assoc) => {
                const accessibleFarms = assoc.fazendas.filter(
                  (f) => f.has_access,
                );
                if (accessibleFarms.length === 0) return null;
                return (
                  <li key={assoc.nomeCliente}>
                    <strong>{assoc.nomeCliente}:</strong>{" "}
                    {accessibleFarms.map((f) => f.name).join(", ")}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </BannerAlert>
  );
}

function UserFormLoading() {
  return (
    <div>
      <SkeletonLoading width="11%" height={24} />
      <SkeletonLoading width="100%" height={144} marginTop={24} />
      <SkeletonLoading width="100%" height={245} marginTop={81} />
      <SkeletonLoading width="100%" height={505} marginTop={81} />
      <SkeletonLoading width="22%" height={53} marginTop={40} />
    </div>
  );
}

const styles = () =>
  createStyles({
    divider: {
      margin: "40px 0",
    },
    benchmarkSection: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },
    benchmarkAlert: {
      marginTop: "8px",
    },
    benchmarkFarmList: {
      margin: "8px 0 0",
      paddingLeft: "20px",
    },
    benchmarkNoAssociations: {
      margin: "8px 0 0",
    },
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDispatchToProps = (dispatch: any) => ({
  notifyError: (payload: Parameters<typeof actions.notifyError>[0]) =>
    dispatch(actions.notifyError(payload)),
  notifySuccess: (payload: Parameters<typeof actions.notifySuccess>[0]) =>
    dispatch(actions.notifySuccess(payload)),
  setLoading: (payload: Parameters<typeof actions.exibirCarregando>[0]) =>
    dispatch(actions.exibirCarregando(payload)),
  resetBenchmarkEditUser: () => dispatch(resetBenchmarkEditUserAction()),
  fetchBenchmarkAssociacoes: (clientId: number) =>
    dispatch(fetchBenchmarkAssociacoes(clientId)),
  fetchBenchmarkStatus: (
    email: string,
  ): Promise<BenchmarkUserStatusResponse | null> =>
    dispatch(fetchBenchmarkStatusForEditUser(email)),
  grantRevokeBenchmarkAccess: (
    email: string,
    body: BenchmarkGrantBody | BenchmarkRevokeBody,
  ): Promise<void> => dispatch(grantRevokeBenchmarkAccess(email, body)),
});

const mapStateToProps = (state: RootState) => ({
  benchmarkLocalUserStatus: state.benchmark.editUser.localUserStatus,
  benchmarkAssociacoes: state.benchmark.editUser.associacoes,
  benchmarkCarregando: state.benchmark.editUser.carregando,
});

export default compose<ComponentType<OwnProps>>(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(UserForm);
